import React from 'react';
import { Swords, HelpCircle, Shuffle } from 'lucide-react';

// Unit type icons from public folder (only the 3 main types for legend)
const UNIT_TYPES = {
  aircraft: { name: 'Aire', icon: '/icons/aircraft.webp' },
  missile: { name: 'Misil', icon: '/icons/missile.webp' },
  tank: { name: 'Tanque', icon: '/icons/tank.webp' }
};

// Determine squadron type based on hero composition
// Rule: 4+ of same type = that type, otherwise = Mixed
const getSquadronType = (heroTypes) => {
  if (!heroTypes) return { type: 'unknown', dominantType: null };
  
  const aircraft = heroTypes.aircraft || 0;
  const missile = heroTypes.missile || 0;
  const tank = heroTypes.tank || 0;
  const total = aircraft + missile + tank;
  
  // If no heroes identified, mark as unknown
  if (total === 0) return { type: 'unknown', dominantType: null };
  
  // Check if any type has 4 or more
  if (aircraft >= 4) return { type: 'aircraft', dominantType: 'aircraft' };
  if (missile >= 4) return { type: 'missile', dominantType: 'missile' };
  if (tank >= 4) return { type: 'tank', dominantType: 'tank' };
  
  // Otherwise it's mixed
  return { type: 'mixed', dominantType: null };
};

// Get squadron label text
const getSquadronLabel = (squadronType) => {
  switch (squadronType.type) {
    case 'aircraft':
      return 'Escuadrón Aire';
    case 'missile':
      return 'Escuadrón Misil';
    case 'tank':
      return 'Escuadrón Tanque';
    case 'mixed':
      return 'Escuadrón Mixto';
    default:
      return 'Escuadrón Desconocido';
  }
};

// Render squadron type icon
const SquadronTypeIcon = ({ squadronType }) => {
  if (squadronType.dominantType && UNIT_TYPES[squadronType.dominantType]?.icon) {
    return (
      <img 
        src={UNIT_TYPES[squadronType.dominantType].icon} 
        alt={UNIT_TYPES[squadronType.dominantType].name} 
        className="w-6 h-6 sm:w-8 sm:h-8 object-contain" 
      />
    );
  }
  
  if (squadronType.type === 'mixed') {
    return <Shuffle className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />;
  }
  
  // Unknown
  return <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />;
};

const AlignmentCard = ({ alignment }) => {
  if (!alignment) return null;

  // Format number to millions with 2 decimals truncated (e.g., 241,996,387 → 241.99M)
  const formatPower = (num) => {
    if (!num) return '0';
    const millions = num / 1000000;
    // Truncate to 2 decimals without rounding
    const truncated = Math.floor(millions * 100) / 100;
    return `${truncated.toFixed(2)}M`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString('es-MX');
  };

  return (
    <div className="space-y-6">
      {/* Total Defensive Power */}
      {alignment.totalDefensivePower && (
        <div className="game-card game-card-gold p-6 text-center">
          <p className="text-cyan-300 text-sm font-bold mb-2">Poder total</p>
          <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400" style={{textShadow: '0 0 30px rgba(250, 204, 21, 0.3)'}}>
            {formatPower(alignment.totalDefensivePower)}
          </p>
        </div>
      )}

      {/* Unit Types Legend - Main types + Mixto + Desconocido */}
      <div className="game-card p-4">
        <p className="text-cyan-400 text-sm font-bold mb-3">TIPOS DE UNIDADES:</p>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-6">
          {Object.entries(UNIT_TYPES).map(([id, type]) => (
            <div key={id} className="flex items-center gap-1 sm:gap-2">
              <img src={type.icon} alt={type.name} className="w-6 h-6 sm:w-8 sm:h-8 object-contain flex-shrink-0" />
              <span className="text-white font-medium text-sm sm:text-base">{type.name}</span>
            </div>
          ))}
          {/* Mixto type */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Shuffle className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 flex-shrink-0" />
            <span className="text-white font-medium text-sm sm:text-base">Mixto</span>
          </div>
          {/* Desconocido type */}
          <div className="flex items-center gap-1 sm:gap-2">
            <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 flex-shrink-0" />
            <span className="text-white font-medium text-sm sm:text-base">Desconocido</span>
          </div>
        </div>
      </div>

      {/* Squadrons - Sorted by power (highest first) */}
      <div className="space-y-4">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          <Swords className="w-5 h-5 text-orange-400" />
          Escuadrones en Muralla
        </h3>
        
        <div className="grid gap-3 sm:gap-4">
          {[...(alignment.squadrons || [])]
            .sort((a, b) => (b.power || 0) - (a.power || 0))
            .map((squadron, index) => {
            const squadronType = getSquadronType(squadron.heroTypes);
            const squadronLabel = getSquadronLabel(squadronType);
            
            return (
              <div key={squadron.position || index} className="game-card p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  {/* Position Number - Use ranking based on power (index+1) */}
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center font-black text-white text-base sm:text-lg flex-shrink-0
                    ${index === 0 ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                      index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                      index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                      'bg-gradient-to-br from-cyan-500 to-blue-500'}`}>
                    {index + 1}
                  </div>
                  
                  {/* Squadron Type with Icon - CENTER */}
                  <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3 min-w-0">
                    <div className="flex-shrink-0">
                      <SquadronTypeIcon squadronType={squadronType} />
                    </div>
                    <div className="text-center min-w-0">
                      <p className="font-bold text-white text-sm sm:text-lg leading-tight">{squadronLabel}</p>
                      <p className="text-xs sm:text-sm text-cyan-300/70">Posición {squadron.position || index + 1}</p>
                    </div>
                  </div>

                  {/* Power */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-cyan-400 font-bold">PODER</p>
                    <p className="text-lg sm:text-2xl font-black text-yellow-400">{formatPower(squadron.power)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AlignmentCard;
